// src/components/CoverPage/LearnMorePage.jsx
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import Button from "./Button";
import "./LearnMorePage.css";

const LearnMorePage = () => {
  const navigate = useNavigate();
  
  const navigateToApp = () => {
    navigate("/app");
  };
  
  return (
    <main className="learn-more-page">
      {/* Hero Section - Dark background similar to other sections */}
      <section className="learn-more-hero">
        <div className="max-w-[1200px] mx-auto px-5 text-center">
          <h1 className="text-5xl sm:text-6xl font-bold mb-6">
            YildX Index Fund - Simplified Crypto Investing
          </h1>
          <p className="text-xl max-w-[800px] mx-auto mb-6 text-white text-opacity-90">
            Gain diversified exposure to the cryptocurrency market through a 
            decentralized index fund with revolutionary governance.
          </p>
        </div>
      </section>

      {/* What is YildX Section */}
      <section className="learn-more-section">
        <div className="max-w-[1200px] mx-auto px-5">
          <h2 className="text-3xl font-bold text-indigo-900 mb-6">What is YildX?</h2>
          <p className="text-lg text-gray-700 mb-8">
            YildX is a decentralized crypto index fund that makes investing in digital assets 
            as simple as buying a single token. Our platform allows you to gain diversified 
            exposure to the cryptocurrency market without having to manage multiple wallets, 
            track various tokens, or worry about optimal entry and exit points.
          </p>
        </div>
      </section>

      {/* How it Works Section */}
      <section className="learn-more-section" style={{background: "#f0f3ff"}}>
        <div className="max-w-[1200px] mx-auto px-5">
          <h2 className="text-3xl font-bold text-indigo-900 mb-6">How YildX Works</h2>
          
          <h3 className="text-2xl font-semibold text-indigo-800 mb-4">Simple Investment Process</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {/* Step 1 */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-xl mb-4">1</div>
              <h4 className="text-xl font-semibold text-indigo-900 mb-2">Deposit ETH</h4>
              <p className="text-gray-600">
                Connect your wallet and deposit Ethereum into the YildX smart contract.
              </p>
            </div>
            
            {/* Step 2 */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-xl mb-4">2</div>
              <h4 className="text-xl font-semibold text-indigo-900 mb-2">Mint Fund Tokens</h4>
              <p className="text-gray-600">
                Your ETH is converted into MCIF tokens that represent fractional ownership of the fund.
              </p>
            </div>
            
            {/* Step 3 */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-xl mb-4">3</div>
              <h4 className="text-xl font-semibold text-indigo-900 mb-2">Automatic Diversification</h4>
              <p className="text-gray-600">
                The fund automatically allocates your investment across a curated basket of top cryptocurrencies.
              </p>
            </div>
            
            {/* Step 4 */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-xl mb-4">4</div>
              <h4 className="text-xl font-semibold text-indigo-900 mb-2">Redeem Anytime</h4>
              <p className="text-gray-600">
                Withdraw your investment back to ETH whenever you want, with no lock-up periods.
              </p>
            </div>
          </div>
          
          <h3 className="text-2xl font-semibold text-indigo-800 mb-4">Transparent Asset Management</h3>
          <p className="text-lg text-gray-700">
            Every MCIF token represents a proportional share of the underlying assets in the fund. 
            The fund's net asset value (NAV) is calculated in real-time based on current market prices, 
            ensuring complete transparency of your investment's value.
          </p>
        </div>
      </section>

      {/* Advantages Section */}
      <section className="learn-more-section">
        <div className="max-w-[1200px] mx-auto px-5">
          <h2 className="text-3xl font-bold text-indigo-900 mb-8">The YildX Advantage</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Advantage 1 */}
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center text-white">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-indigo-900 mb-2">Community-Driven Asset Selection</h3>
                <p className="text-gray-700">
                  The fund's portfolio composition is determined through our decentralized governance mechanism, 
                  where YILDX token holders collectively vote on which assets to include. This community-driven 
                  approach leverages the diverse expertise of governance participants.
                </p>
              </div>
            </div>
            
            {/* Advantage 2 */}
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center text-white">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-indigo-900 mb-2">Automatic Rebalancing</h3>
                <p className="text-gray-700">
                  The fund's portfolio is regularly rebalanced to maintain optimal asset allocation, 
                  saving you time and ensuring your investment stays aligned with market conditions.
                </p>
              </div>
            </div>
            
            {/* Advantage 3 */}
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center text-white">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-indigo-900 mb-2">Lower Transaction Costs</h3>
                <p className="text-gray-700">
                  By pooling resources with other investors, you benefit from reduced gas fees and trading costs 
                  compared to managing multiple assets yourself.
                </p>
              </div>
            </div>
            
            {/* Advantage 4 */}
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center text-white">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-indigo-900 mb-2">Decentralized Security</h3>
                <p className="text-gray-700">
                  Your investment is secured by smart contracts on the blockchain, eliminating traditional 
                  custodial risks and giving you full control over your assets.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Governance Section */}
      <section className="learn-more-section" style={{background: "#f0f3ff"}}>
        <div className="max-w-[1200px] mx-auto px-5">
          <h2 className="text-3xl font-bold text-indigo-900 mb-8">YildX Governance - A Revolutionary Approach</h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
            {/* Left column - Proportional Governance Model */}
            <div>
              <h3 className="text-2xl font-semibold text-indigo-800 mb-4">Proportional Governance Model</h3>
              <p className="text-lg text-gray-700 mb-6">
                YildX introduces a fair governance system that combines the best of traditional finance 
                governance with the innovations of decentralized finance:
              </p>
              
              <ul className="space-y-3 text-gray-700">
                <li className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm">✓</div>
                  <span><strong className="text-indigo-900">Proportional Voting:</strong> Each YILDX token grants voting power proportional to your holdings</span>
                </li>
                <li className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm">✓</div>
                  <span><strong className="text-indigo-900">Democratic Decision-Making:</strong> Token holders vote on key decisions affecting the fund</span>
                </li>
                <li className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm">✓</div>
                  <span><strong className="text-indigo-900">Delegation System:</strong> Delegate your voting power to trusted representatives</span>
                </li>
              </ul>
            </div>
            
            {/* Right column - Passive Income */}
            <div>
              <h3 className="text-2xl font-semibold text-indigo-800 mb-4">Passive Income Through Governance</h3>
              <p className="text-lg text-gray-700 mb-6">
                Unlike traditional funds where fees only benefit the fund managers, YildX distributes 70% 
                of all collected fees to governance token holders, with the remaining 30% allocated to the fund's 
                gas fund to cover operational expenses:
              </p>
              
              <ul className="space-y-3 text-gray-700">
                <li className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm">$</div>
                  <span><strong className="text-indigo-900">Mint/Burn Fees:</strong> When investors enter or exit the fund (0.25% entry/exit fee)</span>
                </li>
                <li className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm">$</div>
                  <span><strong className="text-indigo-900">Management Fees:</strong> Collected periodically based on assets under management (2% annual fee)</span>
                </li>
                <li className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm">$</div>
                  <span><strong className="text-indigo-900">Fee Distribution:</strong> 70% to governance token holders, 30% to the gas fund</span>
                </li>
                <li className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm">$</div>
                  <span><strong className="text-indigo-900">Gas Fund Purpose:</strong> Covers transaction costs, rebalancing operations, and other on-chain expenses</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
            {/* Types of Decisions */}
            <div>
              <h3 className="text-2xl font-semibold text-indigo-800 mb-4">Types of Decisions Governed</h3>
              <p className="text-lg text-gray-700 mb-6">
                YILDX token holders can propose and vote on:
              </p>
              
              <ul className="space-y-3 text-gray-700">
                <li className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm">•</div>
                  <span><strong className="text-indigo-900">Fee Adjustments:</strong> Changing entry, exit, and management fees</span>
                </li>
                <li className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm">•</div>
                  <span><strong className="text-indigo-900">Portfolio Composition:</strong> Adding or removing tokens from the index</span>
                </li>
                <li className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm">•</div>
                  <span><strong className="text-indigo-900">Asset Allocation:</strong> Adjusting the percentage allocated to each token</span>
                </li>
                <li className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm">•</div>
                  <span><strong className="text-indigo-900">Protocol Upgrades:</strong> Implementing improvements to the fund's operation</span>
                </li>
              </ul>
            </div>
            
            {/* Auto-Approval */}
            <div>
              <h3 className="text-2xl font-semibold text-indigo-800 mb-4">Auto-Approval Mechanism</h3>
              <p className="text-lg text-gray-700 mb-6">
                Our innovative governance system includes an auto-approval mechanism that streamlines decision-making:
              </p>
              
              <ul className="space-y-3 text-gray-700">
                <li className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm">•</div>
                  <span><strong className="text-indigo-900">Majority Threshold:</strong> Proposals automatically pass when they reach sufficient voting support</span>
                </li>
                <li className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm">•</div>
                  <span><strong className="text-indigo-900">Strong Majority Rule:</strong> Proposals with overwhelming support are fast-tracked</span>
                </li>
                <li className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm">•</div>
                  <span><strong className="text-indigo-900">Efficient Operations:</strong> No delays or governance bottlenecks</span>
                </li>
              </ul>
            </div>
          </div>
          
          {/* Why This Matters */}
          <div className="bg-white p-8 rounded-xl shadow-md">
            <h3 className="text-2xl font-semibold text-indigo-900 mb-6">Why This Governance Model Matters to Investors</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Point 1 */}
              <div className="bg-indigo-50 p-6 rounded-lg">
                <h4 className="text-xl font-semibold text-indigo-900 mb-3">1. Alignment of Interests</h4>
                <p className="text-gray-700">
                  When governance token holders benefit directly from the fund's success through fee distribution, 
                  they're incentivized to make decisions that benefit all participants. This creates a self-reinforcing 
                  ecosystem where what's good for governance is good for investors.
                </p>
              </div>
              
              {/* Point 2 */}
              <div className="bg-indigo-50 p-6 rounded-lg">
                <h4 className="text-xl font-semibold text-indigo-900 mb-3">2. Decentralized but Professional</h4>
                <p className="text-gray-700">
                  Our model strikes the perfect balance between the democratic principles of DeFi and the 
                  professional management of traditional finance. Proportional voting ensures that those with the most 
                  at stake have an appropriate level of influence.
                </p>
              </div>
              
              {/* Point 3 */}
              <div className="bg-indigo-50 p-6 rounded-lg">
                <h4 className="text-xl font-semibold text-indigo-900 mb-3">3. Sustainable Fee Structure</h4>
                <p className="text-gray-700">
                  By distributing 70% of all fees to governance token holders and allocating 30% to the gas fund, 
                  we create a sustainable ecosystem where community members become stakeholders in the fund's 
                  long-term success while ensuring the protocol can cover its own operational costs.
                </p>
              </div>
              
              {/* Point 4 */}
              <div className="bg-indigo-50 p-6 rounded-lg">
                <h4 className="text-xl font-semibold text-indigo-900 mb-3">4. True Fund Ownership</h4>
                <p className="text-gray-700">
                  Unlike traditional investment vehicles where you're just a customer, YildX gives you the 
                  opportunity to become a true owner in the fund's operation through governance participation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fund Operator Section */}
      <section className="learn-more-section" style={{background: "#f0f3ff"}}>
        <div className="max-w-[1200px] mx-auto px-5">
          <h2 className="text-3xl font-bold text-indigo-900 mb-8">Fund Operator Role</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Fund Operator Description */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-2xl font-semibold text-indigo-800 mb-4">Who Is the Fund Operator?</h3>
              <p className="text-lg text-gray-700 mb-4">
                The YildX protocol designates a fund operator who handles critical day-to-day operations requiring technical expertise and rapid response capabilities. <strong>The operator is elected by YILDX token holders</strong> and can be replaced through governance voting if needed.
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm">•</div>
                  <span>Executes approved governance decisions</span>
                </li>
                <li className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm">•</div>
                  <span>Performs emergency rebalancing during market volatility</span>
                </li>
                <li className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm">•</div>
                  <span>Manages technical upgrades to smart contracts</span>
                </li>
                <li className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm">•</div>
                  <span>Ensures continuous protocol functionality</span>
                </li>
              </ul>
            </div>
            
            {/* Why This Structure */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-2xl font-semibold text-indigo-800 mb-4">Why This Structure?</h3>
              <p className="text-lg text-gray-700 mb-4">
                This hybrid governance model combines the benefits of decentralized decision-making with operational efficiency:
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm">•</div>
                  <span><strong className="text-indigo-900">Decentralized Strategy:</strong> Token holders determine fund direction and asset strategy</span>
                </li>
                <li className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm">•</div>
                  <span><strong className="text-indigo-900">Efficient Execution:</strong> Professional operator handles technical implementation</span>
                </li>
                <li className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm">•</div>
                  <span><strong className="text-indigo-900">Community Control:</strong> Token holders can vote to add or remove operators at any time</span>
                </li>
                <li className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm">•</div>
                  <span><strong className="text-indigo-900">Transparent Accountability:</strong> All operator actions are visible on-chain and subject to community review</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How to Participate */}
      <section className="learn-more-section">
        <div className="max-w-[1200px] mx-auto px-5">
          <h2 className="text-3xl font-bold text-indigo-900 mb-8">How to Participate in Governance</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {/* Step 1 */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-xl mb-4">1</div>
              <h4 className="text-xl font-semibold text-indigo-900 mb-2">Acquire YILDX Tokens</h4>
              <p className="text-gray-600">
                Purchase tokens during our token sale or on the open market.
              </p>
            </div>
            
            {/* Step 2 */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-xl mb-4">2</div>
              <h4 className="text-xl font-semibold text-indigo-900 mb-2">Hold or Delegate</h4>
              <p className="text-gray-600">
                Hold YILDX tokens for direct voting or delegate your voting power to trusted representatives.
              </p>
            </div>
            
            {/* Step 3 */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-xl mb-4">3</div>
              <h4 className="text-xl font-semibold text-indigo-900 mb-2">Vote on Proposals</h4>
              <p className="text-gray-600">
                Use your voting power to help shape the future of the fund.
              </p>
            </div>
            
            {/* Step 4 */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-xl mb-4">4</div>
              <h4 className="text-xl font-semibold text-indigo-900 mb-2">Receive Fee Distributions</h4>
              <p className="text-gray-600">
                Automatically receive your share of collected fees.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Token Info Section */}
      <section className="learn-more-section" style={{background: "#f0f3ff"}}>
        <div className="max-w-[1200px] mx-auto px-5">
          <h2 className="text-3xl font-bold text-indigo-900 mb-8">YILDX Token Economics</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Token Distribution */}
            <div>
              <h3 className="text-2xl font-semibold text-indigo-800 mb-4">Token Distribution</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm">•</div>
                  <span><strong className="text-indigo-900">Total Supply:</strong> 60,000,000 YILDX tokens</span>
                </li>
                <li className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm">•</div>
                  <span><strong className="text-indigo-900">Token Sale:</strong> 15,000,000 YILDX tokens (25%)</span>
                </li>
                <li className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm">•</div>
                  <span><strong className="text-indigo-900">Treasury:</strong> 45,000,000 YILDX tokens (75%)</span>
                </li>
              </ul>
            </div>
            
            {/* Token Utility */}
            <div>
              <h3 className="text-2xl font-semibold text-indigo-800 mb-4">Token Utility</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm">•</div>
                  <span><strong className="text-indigo-900">Governance:</strong> Vote on fund parameters and asset selection</span>
                </li>
                <li className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm">•</div>
                  <span><strong className="text-indigo-900">Fee Earnings:</strong> Receive 70% of all protocol fees</span>
                </li>
                <li className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm">•</div>
                  <span><strong className="text-indigo-900">Delegation:</strong> Delegate voting power to trusted experts</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Optimism Expansion Section */}
      <section className="learn-more-section">
        <div className="max-w-[1200px] mx-auto px-5">
          <h2 className="text-3xl font-bold text-indigo-900 mb-8">Expanding to Optimism</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Migration Benefits */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-2xl font-semibold text-indigo-800 mb-4">Why Optimism?</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm">→</div>
                  <span><strong className="text-indigo-900">Lower Fees:</strong> Significantly reduced transaction costs</span>
                </li>
                <li className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm">→</div>
                  <span><strong className="text-indigo-900">Faster Transactions:</strong> Near-instant confirmations</span>
                </li>
                <li className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm">→</div>
                  <span><strong className="text-indigo-900">Ethereum Security:</strong> Inherits the security of Ethereum mainnet</span>
                </li>
              </ul>
            </div>
            
            {/* New Features */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-2xl font-semibold text-indigo-800 mb-4">New Features Coming</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm">→</div>
                  <span><strong className="text-indigo-900">Perpetual Swap Strategy:</strong> Higher yield generation</span>
                </li>
                <li className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm">→</div>
                  <span><strong className="text-indigo-900">Enhanced Governance:</strong> Improved voting and delegation</span>
                </li>
                <li className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm">→</div>
                  <span><strong className="text-indigo-900">User-Owned Funds:</strong> Create your own custom index fund</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Dark background like other sections */}
      <section className="learn-more-dark-section py-16">
        <div className="max-w-[1200px] mx-auto px-5 text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">Ready to Start Investing?</h2>
          <p className="text-xl mb-10 max-w-[800px] mx-auto text-white text-opacity-90">
            Join YildX today and experience the future of crypto investing—diversified, 
            transparent, and community-governed.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              variant="primary" 
              onClick={navigateToApp}
            >
              See App
            </Button>
            <Link to="/">
              <Button variant="secondary">
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default LearnMorePage;